import React, { useRef, useState } from 'react';
import { apiFetch } from '../api';
import BadgeIcon from './BadgeIcon';
import './BadgeUploader.css';

function badgeFromEntity(entity) {
  if (!entity) return null;
  if (entity.badge_url || entity.badge_svg) {
    return { url: entity.badge_url || null, svg: entity.badge_svg || null };
  }
  return null;
}

function BadgeUploader({
  kind,
  entityId,
  entity,
  canEdit,
  onUpdated,
  onError,
  label = 'Chat badge',
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const badge = badgeFromEntity(entity);
  const uploadPath = kind === 'club'
    ? `/admin/clubs/${entityId}/badge/upload`
    : `/admin/roles/${entityId}/badge/upload`;
  const deletePath = kind === 'club'
    ? `/admin/clubs/${entityId}/badge`
    : `/admin/roles/${entityId}/badge`;

  const handleUpload = async (file) => {
    if (!file || !canEdit) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('badge', file);
      const res = await apiFetch(uploadPath, { method: 'POST', body: form });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.message || 'Upload failed');
      onUpdated?.(data.role || data.club);
    } catch (err) {
      onError?.(err.message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!canEdit || !badge) return;
    setDeleting(true);
    try {
      const res = await apiFetch(deletePath, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.message || 'Delete failed');
      onUpdated?.(data.role || data.club);
    } catch (err) {
      onError?.(err.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="badge-uploader">
      <h4>{label}</h4>
      <p className="badge-uploader-hint">
        Upload SVG or a raster image 32×32 px or smaller. One file per {kind}; a new upload replaces the old one.
      </p>
      <div className="badge-uploader-preview">
        {badge ? (
          <>
            <BadgeIcon badge={badge} />
            <span className="badge-uploader-filename">{entity?.badge_url || 'inline SVG'}</span>
          </>
        ) : (
          <span className="badge-uploader-empty">No badge set</span>
        )}
      </div>
      {canEdit && (
        <div className="badge-uploader-actions">
          <input
            ref={inputRef}
            type="file"
            accept="image/svg+xml,image/png,image/jpeg,image/gif,image/webp,.svg"
            className="badge-uploader-input"
            onChange={(e) => handleUpload(e.target.files?.[0])}
            disabled={uploading || deleting}
          />
          <button
            type="button"
            className="badge-uploader-btn"
            onClick={() => inputRef.current?.click()}
            disabled={uploading || deleting}
          >
            {uploading ? 'Uploading…' : 'Upload badge'}
          </button>
          {badge && (
            <button
              type="button"
              className="badge-uploader-btn badge-uploader-btn--danger"
              onClick={handleDelete}
              disabled={uploading || deleting}
            >
              {deleting ? 'Removing…' : 'Delete badge'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default BadgeUploader;